import { IncidentCard as ChartCard } from '@features/reports/components/incidents/dashboard/IncidentCard'
import {
  PERIOD_TIME_KEY,
  periodSeriesToRows
} from '@features/reports/utils/periodSeriesToRows'
import {
  type ChartTooltipRender,
  LineChart,
  PieChart,
  type PieChartSlice
} from '@lmc/design-system'
import {
  GoalReviewStatus,
  GoalStatus,
  type GoalsDashboard,
  type Home,
  LineChartOptions
} from '@lmc/ts-common'
import { theme } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import { Col, Radio, type RadioChangeEvent, Row } from 'antd'
import { getNominative } from 'common/utils'
import { Spinner } from 'components/generic'
import { GOALS } from 'components/routes/residents/goals'
import dayjs from 'dayjs'
import type { TFunction } from 'i18next'
import { NAMESPACE } from 'locale'
import { goalReportsConstants } from 'locale/goalReports'
import { capitalize } from 'lodash'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import styled from 'styled-components'
import type { RangeValue } from 'types/shared'

interface GoalsChartsData {
  chartsData: GoalsDashboard | null
  dateRange: RangeValue<dayjs.Dayjs | string>
  resident?: string | null | undefined
  isLoading: boolean
  overdueGoalsLink?: boolean
  testId?: string
}

const HeaderRadioGroup = ({
  selectedValue,
  handleChange,
  options
}: {
  selectedValue: LineChartOptions
  handleChange: (e: RadioChangeEvent) => void
  options: { label: string; value: string }[]
}) => (
  <HeaderGroupStyled value={selectedValue} onChange={handleChange}>
    {options.map((option) => (
      <Radio.Button key={option.value} value={option.value}>
        {option.label}
      </Radio.Button>
    ))}
  </HeaderGroupStyled>
)

const GoalsCharts = ({
  chartsData,
  dateRange,
  resident,
  isLoading = false,
  overdueGoalsLink = true,
  testId
}: GoalsChartsData) => {
  const { t } = useTranslation(NAMESPACE.GOAL_REPORTS)

  const preferredPeopleNominative = useSelector(
    (state: { global: { home: Home } }) =>
      state.global.home.preferredPeopleNominative
  )

  const preferredNominative = getNominative(
    preferredPeopleNominative,
    t(goalReportsConstants.INDIVIDUALS)
  )

  const [selectedValue, setSelectedValue] = useState<LineChartOptions>(
    LineChartOptions.GoalsCompleted
  )

  const options = [
    {
      label: t(goalReportsConstants.GOALS_COMPLETED),
      description: '',
      value: LineChartOptions.GoalsCompleted
    },
    {
      label: t(goalReportsConstants.PERCENTAGE_ADDED),
      description: t(goalReportsConstants.PERCENTAGE_ADDED_DESCRIPTION),
      value: LineChartOptions.AverageProgressAdded
    },
    {
      label: t(goalReportsConstants.LOGS_LINKED),
      description: t(goalReportsConstants.LOGS_LINKED_DESCRIPTION),
      value: LineChartOptions.TotalLogsLinked
    }
  ]

  const handleChange = (e: RadioChangeEvent) => {
    setSelectedValue(e.target.value)
  }

  const getHeader = (type: 'label' | 'description') => {
    const selectedOption = options.find(
      (option) => option.value === selectedValue
    )
    return selectedOption?.[type] || ''
  }

  const axisMapper = {
    [LineChartOptions.GoalsCompleted]: t(goalReportsConstants.GOALS_COMPLETED),
    [LineChartOptions.TotalLogsLinked]: t(goalReportsConstants.LOGS),
    [LineChartOptions.AverageProgressAdded]: t(goalReportsConstants.PERCENTAGE)
  }

  const mappedPieData: PieChartSlice[] =
    chartsData?.goalsStatusData?.map((item) => ({
      colour: STATUS_COLOURS[item.name],
      name: capitalize(t(item.name)),
      value: item.statusCount
    })) || []

  // One point per month, counted from the start of the range's first month.
  const lineChart = periodSeriesToRows(
    [
      {
        data: chartsData?.lineChartsData?.[selectedValue] || [],
        name: t(goalReportsConstants[selectedValue])
      }
    ],
    dayjs(dateRange?.[0]).startOf('month'),
    'month'
  )

  return (
    <>
      <RowContainer
        gutter={24}
        data-testid={testId ? `${testId}-goals-charts` : 'goals-charts'}
      >
        <ColContainer xs={24} md={12}>
          <ChartCard
            title={t(goalReportsConstants.GOAL_STATUS)}
            subtitle={t(goalReportsConstants.GOAL_STATUS_DESCRIPTION)}
            divider
            testId={
              testId
                ? `${testId}-goals-charts-status-card`
                : 'goals-charts-status-card'
            }
          >
            {chartsData?.goalsStatusData && !isLoading ? (
              <div
                data-testid={
                  testId ? `${testId}-goals-charts-donut` : 'goals-charts-donut'
                }
              >
                <PieChart
                  aria-label={t(goalReportsConstants.GOAL_STATUS)}
                  centre={{ caption: t(goalReportsConstants.TOTAL_GOALS) }}
                  data={mappedPieData}
                  emptyMessage={t(goalReportsConstants.EMPTY_STATE)}
                  legend="left"
                  legendTitle={t(goalReportsConstants.GOALS)}
                  legendValues
                />
              </div>
            ) : (
              <PieChartSpinnerContainer>
                <Spinner />
              </PieChartSpinnerContainer>
            )}
          </ChartCard>
        </ColContainer>
        <ColContainer xs={24} md={12} extraMargin>
          <ChartCard
            title={t(goalReportsConstants.GOAL_STATISTICS)}
            subtitle={t(goalReportsConstants.GOAL_STATISTICS_DESCRIPTION)}
            divider
            testId={
              testId
                ? `${testId}-goals-charts-stats-card`
                : 'goals-charts-stats-card'
            }
          >
            <Stats
              data-testid={
                testId ? `${testId}-goals-charts-stats` : 'goals-charts-stats'
              }
            >
              <Results level={2}>{t(goalReportsConstants.RESULTS)}</Results>
              <TextContainer>
                <StyledText>
                  {t(goalReportsConstants.AVERAGE_PROGRESS)}
                </StyledText>
                <StyledText weight="medium">
                  {!isLoading && chartsData?.goalsStats?.averageProgressAdded}%
                </StyledText>
              </TextContainer>
              <TextContainer>
                <StyledText>
                  {t(goalReportsConstants.TOTAL_MILESTONES_COMPLETED)}
                </StyledText>
                <StyledText weight="medium">
                  {!isLoading && chartsData?.goalsStats?.completedMilestones}
                </StyledText>
              </TextContainer>
              <TextContainer>
                <StyledText>{t(goalReportsConstants.AVERAGE_LOGS)}</StyledText>
                <StyledText weight="medium">
                  {!isLoading && chartsData?.goalsStats?.averageLogCount}
                </StyledText>
              </TextContainer>
              {!resident && (
                <>
                  <TextContainer>
                    <StyledText>
                      {t(goalReportsConstants.GOAL_IN_PROGRESS, {
                        preferredTerm: preferredNominative
                      })}
                    </StyledText>
                    <StyledText weight="medium">
                      {!isLoading &&
                        `${chartsData?.goalsStats?.suWithGoalInProgress}/${chartsData?.goalsStats?.totalActiveResidents}`}
                    </StyledText>
                  </TextContainer>
                  <TextContainer>
                    <StyledText>
                      {t(goalReportsConstants.GOAL_OVERDUE, {
                        preferredTerm: preferredNominative
                      })}
                    </StyledText>
                    {overdueGoalsLink ? (
                      <Link
                        to={{
                          pathname: GOALS.MANAGEMENT
                        }}
                        state={{ reviewStatus: [GoalReviewStatus.overdue] }}
                      >
                        <StyledLink weight="medium">
                          {!isLoading &&
                            chartsData?.goalsStats?.suWithOverdueGoal}
                        </StyledLink>
                      </Link>
                    ) : (
                      <StyledText weight="medium">
                        {!isLoading &&
                          chartsData?.goalsStats?.suWithOverdueGoal}
                      </StyledText>
                    )}
                  </TextContainer>
                </>
              )}
            </Stats>
          </ChartCard>
        </ColContainer>
      </RowContainer>
      <RowContainer
        data-testid={
          testId
            ? `${testId}-goals-charts-line-container`
            : 'goals-charts-line-container'
        }
      >
        <ChartCard
          title={getHeader('label')}
          subtitle={getHeader('description')}
          headerAction={
            <HeaderRadioGroup
              selectedValue={selectedValue}
              handleChange={handleChange}
              options={options}
            />
          }
          testId={
            testId
              ? `${testId}-goals-charts-line-card`
              : 'goals-charts-line-card'
          }
        >
          {chartsData?.lineChartsData && !isLoading ? (
            <div
              data-testid={
                testId ? `${testId}-goals-charts-line` : 'goals-charts-line'
              }
            >
              <LineChart
                aria-label={getHeader('label')}
                data={lineChart.rows}
                emptyMessage={t(goalReportsConstants.EMPTY_STATE)}
                height={350}
                series={lineChart.series.map((series) => ({
                  ...series,
                  colour: LINE_COLOUR
                }))}
                tooltip={tooltipRenders[selectedValue](t, preferredNominative)}
                xAxis={{
                  domain: [
                    dayjs(dateRange?.[0]).startOf('month').valueOf(),
                    dayjs(dateRange?.[1]).endOf('month').valueOf()
                  ],
                  title: t(goalReportsConstants.TIME)
                }}
                xKey={PERIOD_TIME_KEY}
                yAxis={{ title: axisMapper[selectedValue] }}
              />
            </div>
          ) : (
            <LineChartSpinnerContainer>
              <Spinner />
            </LineChartSpinnerContainer>
          )}
        </ChartCard>
      </RowContainer>
    </>
  )
}

const ColContainer = styled(Col)<{ extraMargin?: boolean }>`
  ${({ theme }) => theme.mediaQueries?.mobileDown} {
    min-width: 555px;

    ${({ extraMargin }) => extraMargin && `margin-top: 24px; `}
  }
`

const RowContainer = styled(Row)`
  ${({ theme }) => theme.mediaQueries?.mobileDown} {
    overflow: scroll;
    &::-webkit-scrollbar {
      display: none;
    }
  }
`

const HeaderGroupStyled = styled(Radio.Group)`
  flex-direction: row;

  ${({ theme }) => theme.mediaQueries?.mobileDown} {
    flex-direction: column;
    width: 250px;
    padding: 0 40px;
  }
`

const StyledText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color3};
`

const StyledLink = styled(Text)`
  color: ${({ theme }) => theme.color.status.primary};
  text-decoration: underline;
`

const TextContainer = styled.div`
  display: flex;
  justify-content: space-between;
`

const Results = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color5};
  align-self: end;
`

const Stats = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
  padding-top: 24px;
`

const PieChartSpinnerContainer = styled.div`
  height: 250px;
`

const TooltipHeading = styled.div`
  font-weight: bold;
`

const LineChartSpinnerContainer = styled.div`
  width: 723px;
  height: 200px;
`

const STATUS_COLOURS: Record<GoalStatus, string> = {
  [GoalStatus.archive]: theme.color.neutral.color4,
  [GoalStatus.done]: theme.color.status.success,
  [GoalStatus.inProgress]: theme.color.status.primary,
  [GoalStatus.notStarted]: theme.color.status.warning,
  [GoalStatus.paused]: theme.color.category.healthRecords
}

const LINE_COLOUR = '#46A182'

const tooltipRenders: Record<
  LineChartOptions,
  (t: TFunction, nominative: string) => ChartTooltipRender
> = {
  averageProgressAdded:
    (t, nominative) =>
    ({ items }) => (
      <>
        <TooltipHeading>
          {t(goalReportsConstants.TOOLTIP_PROGRESS_HEADING)}
        </TooltipHeading>
        <div>
          {t(goalReportsConstants.TOOLTIP_PROGRESS, {
            nominative,
            value: items[0]?.value
          })}
        </div>
      </>
    ),

  goalsCompleted:
    (t, nominative) =>
    ({ items, label }) => (
      <>
        <TooltipHeading>{dayjs(label).format('MMMM YYYY')}</TooltipHeading>
        <div>
          {t(goalReportsConstants.TOOLTIP_COMPLETED, {
            nominative,
            value: items[0]?.value
          })}
        </div>
      </>
    ),

  totalLogsLinked:
    (t) =>
    ({ items }) => (
      <>
        <TooltipHeading>
          {t(goalReportsConstants.TOOLTIP_LOGS_HEADING)}
        </TooltipHeading>
        <div>
          {t(goalReportsConstants.TOOLTIP_LOGS, { value: items[0]?.value })}
        </div>
      </>
    )
}

export default GoalsCharts
